import {
  Column,
  CreateDateColumn,
  Entity,
  JoinColumn,
  ManyToOne,
  PrimaryGeneratedColumn,
  UpdateDateColumn
} from "typeorm";
import { Travel } from "./reg_travels.entity";
import { WaterAdditionTravelSummary } from "../interfaces/water-addition.interface";

@Entity("reg_water_additions")
export class WaterAddition {

  @PrimaryGeneratedColumn("uuid")
    id_water_addition?: string;

  @Column({
    type: "numeric",
    nullable: false,
    default: 0,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    water_added?: number;

  @Column({
    type: "numeric",
    nullable: false,
    default: 0,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    start_total_water_added?: number;

  @Column({ type: "timestamp", default: "(now() at time zone 'utc')" })
    start_date?: Date;

  @Column({ type: "timestamp", nullable: true })
    finish_date?: Date;

  @Column({
    type: "numeric",
    nullable: false,
    default: 1.00,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    water_correction_factor?: number;

  @CreateDateColumn({ default: "(now() at time zone 'utc')" })
    registration_date?: Date;

  @UpdateDateColumn({ default: "(now() at time zone 'utc')" })
    updated_date?: Date;

  /**
   * Flag: the row is post-discharge water that, under the global parameter
   * `WATER_POST_DISCHARGE_NEXT_TRAVEL_ENABLED`, belongs to the NEXT travel of the vehicle.
   * Drives the segmented "post-discharge from previous travel" display/badge.
   */
  @Column({ type: "boolean", nullable: false, default: false })
    is_reassigned_post_discharge?: boolean;

  /**
   * Transient state: `true` while the post-discharge water waits for the next travel
   * (still linked to the origin travel for storage, but HIDDEN from the origin travel's reads);
   * set to `false` once reassigned to the destination travel. Also guarantees idempotency.
   */
  @Column({ type: "boolean", nullable: false, default: false })
    pending_reassignment?: boolean;

  @ManyToOne(() => Travel, (travel) => travel.water_additions, { nullable: false })
  @JoinColumn({ name: "id_travel_fk" })
    travel?: Travel;

  /**
   * Traceability link: the travel where the post-discharge addition physically
   * occurred. Set when the addition is reassigned to the next travel; the key used to find
   * pending additions of the previous travel.
   */
  @ManyToOne(() => Travel, { nullable: true })
  @JoinColumn({ name: "id_origin_travel_fk" })
    origin_travel?: Travel;

  /**
   * Determines the moment based on the water addition start date and the travel's last due unload date.
   * @param {Object} waterAddition - Object containing information about water addition.
   * @param {Date} [waterAddition.start_date] - The start date of water addition.
   * @param {Object} travel - Object containing information about travel.
   * @param {Date | null} [travel.last_due_unload_date] - The last due unload date of travel.
   * @return {"PRE_DISCHARGE" | "POST_DISCHARGE" | null} Returns "PRE_DISCHARGE" if the water addition start date is before or equal to the last
   * due unload date# "POST_DISCHARGE" if the water addition start date is after the last due unload date, or null if the required dates are missing.
   */
  static getMoment(waterAddition: { start_date?: Date }, travel: { last_due_unload_date?: Date | null }): "PRE_DISCHARGE" | "POST_DISCHARGE" | null {
    return (waterAddition?.start_date && travel?.last_due_unload_date)
      ? waterAddition.start_date > travel.last_due_unload_date
        ? "POST_DISCHARGE"
        : "PRE_DISCHARGE"
      : null;
  }

  /**
   * The effective moment of a single water addition — the canonical rule, on top of {@link getMoment}: a
   * post-discharge addition reassigned from a previous travel is reused in this travel's load, so it counts
   * as PRE-discharge here (the reassigned flag wins). Null when the dates are missing, like getMoment.
   *
   * @param waterAddition The addition (its start_date and reassignment flag).
   * @param travel The travel with its last_due_unload_date.
   * @returns "PRE_DISCHARGE" | "POST_DISCHARGE" | null.
   */
  static resolveMoment(
    waterAddition: { start_date?: Date; is_reassigned_post_discharge?: boolean },
    travel: { last_due_unload_date?: Date | null }
  ): "PRE_DISCHARGE" | "POST_DISCHARGE" | null {
    return waterAddition?.is_reassigned_post_discharge
      ? "PRE_DISCHARGE"
      : WaterAddition.getMoment(waterAddition, travel);
  }

  /**
   * Calculates water addition summary for a travel
   * @param waterAdditions Array of water additions for the travel
   * @param travel Travel object with last_due_unload_date
   * @returns Summary object with pre/post discharge totals
   */
  static calculateTravelWaterSummary(
    waterAdditions: WaterAddition[],
    travel: { last_due_unload_date?: Date | null }
  ): WaterAdditionTravelSummary {

    // The moment (with the reassignment rule) is resolved in one place — see resolveMoment. An addition with
    // no resolvable moment (missing dates) falls in neither total, though it still counts in the total count.
    const preDischargeAdditions = waterAdditions.filter(
      (wa) => WaterAddition.resolveMoment(wa, travel) !== "POST_DISCHARGE"
    );
    const postDischargeAdditions = waterAdditions.filter(
      (wa) => WaterAddition.resolveMoment(wa, travel) === "POST_DISCHARGE"
    );

    const preDischargeTotal = preDischargeAdditions.reduce((sum, wa) => sum + (wa.water_added || 0), 0);
    const postDischargeTotal = postDischargeAdditions.reduce((sum, wa) => sum + (wa.water_added || 0), 0);

    return {
      pre_discharge_total: preDischargeTotal,
      post_discharge_total: postDischargeTotal,
      total_water_added: preDischargeTotal + postDischargeTotal,
      water_additions_count: waterAdditions.length,
      pre_discharge_count: preDischargeAdditions.length,
      post_discharge_count: postDischargeAdditions.length
    };
  }

}
